/**
 * AuthContext.tsx — React context wiring Cognito session to component tree
 *
 * AuthProvider:
 *   - On mount, calls getSession() to hydrate from localStorage (persists across refresh, AUTH-02)
 *   - Exposes { user, loading, signIn, signOut, completeNewPassword }
 *   - signIn() calls authService.signIn then sets user from the returned session's ID token
 *   - signOut() calls authService.signOut then clears user
 *   - completeNewPassword() completes the AdminCreateUser force-change challenge (D-09)
 *
 * user is populated from the Cognito ID token (email, custom:plan live there;
 * the access token is used only for API Bearer headers in api.ts).
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import type { CognitoUserSession } from 'amazon-cognito-identity-js'
import {
  signIn as serviceSignIn,
  signOut as serviceSignOut,
  completeNewPassword as serviceCompleteNewPassword,
  getSession,
} from './authService'
import { loadRuntimeConfig } from './runtimeConfig'
import { identifyUser, resetAnalytics, track } from '../lib/analytics'
import { authedFetch } from '../lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  sub: string
  email?: string
  /** ROLES dimension (authorization) — from the `cognito:groups` claim. Empty when none. */
  roles: string[]
  /** Convenience: roles includes the `admins` group. */
  isAdmin: boolean
  /** PLAN dimension (commercial entitlement) — from the `custom:plan` claim. */
  plan?: string
}

/** The Cognito group that grants operator/admin authorization. Mirrors API authz.ADMIN_ROLE. */
export const ADMIN_ROLE = 'admins'

export interface AuthContextValue {
  /** Authenticated user (from Cognito ID token) or null when signed out. */
  user: AuthUser | null
  /** True while the initial getSession() check is in-flight (prevents flash of login form). */
  loading: boolean
  /** Sign in via USER_SRP_AUTH. Resolves when the session is established. */
  signIn: (email: string, password: string) => Promise<void>
  /** Invalidate the Cognito session server-side (globalSignOut, AUTH-03). */
  signOut: () => Promise<void>
  /**
   * Complete the NEW_PASSWORD_REQUIRED challenge after AdminCreateUser (D-09).
   * Call after signIn rejects with 'NEW_PASSWORD_REQUIRED'.
   */
  completeNewPassword: (newPassword: string) => Promise<void>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextValue | null>(null)

/** Internal hook — use useAuth() (from useAuth.ts) in components. */
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be called inside <AuthProvider>')
  return ctx
}

// ---------------------------------------------------------------------------
// Session → user mapping (ID token carries email + custom:plan)
// ---------------------------------------------------------------------------

export function sessionToUser(session: CognitoUserSession): AuthUser {
  // The ID token carries both dimensions: cognito:groups (ROLES) and custom:plan (PLAN), plus
  // sub/email. (The access token also carries cognito:groups; we read the ID token here because
  // it is the one that reliably carries custom:plan too — see docs/RBAC.md.)
  const payload = session.getIdToken().decodePayload() as Record<string, unknown>
  const groups = payload['cognito:groups']
  const roles = Array.isArray(groups)
    ? groups.filter((g): g is string => typeof g === 'string')
    : []
  return {
    sub: String(payload['sub'] ?? ''),
    email: payload['email'] !== undefined ? String(payload['email']) : undefined,
    roles,
    isAdmin: roles.includes(ADMIN_ROLE),
    plan: payload['custom:plan'] !== undefined ? String(payload['custom:plan']) : undefined,
  }
}

// ---------------------------------------------------------------------------
// Dev-only login bypass
// ---------------------------------------------------------------------------
// Mirrors the server's DEV_AUTH_BYPASS so the team can reach the map locally
// without a Cognito pool (the day-1 login wall otherwise blocks it). HARD-GUARDED
// by import.meta.env.PROD — Vite statically replaces this with `false` in a
// production build, so the branch is dead-code-eliminated and can NEVER activate
// in prod, regardless of the VITE_DEV_AUTH_BYPASS value. Same guard pattern as
// the dev path in lib/api.ts (T-01-27).
const DEV_AUTH_BYPASS = !import.meta.env.PROD && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

// T-368: the dev user is a plain non-admin by default (mirroring the server bypass), so the
// admin panel is invisible locally exactly as it is for a real non-admin. VITE_DEV_AUTH_ADMIN
// opts it into the admins group for building/reviewing that panel before the Cognito pool
// exists. Guarded by the same import.meta.env.PROD check — dead code in a production build,
// and the API enforces the role independently, so this can only ever change what is DISPLAYED.
const DEV_AUTH_ADMIN = !import.meta.env.PROD && import.meta.env.VITE_DEV_AUTH_ADMIN === 'true'

const DEV_BYPASS_USER: AuthUser = {
  sub: 'dev-user-001',
  email: 'dev@optiyield.local',
  roles: DEV_AUTH_ADMIN ? [ADMIN_ROLE] : [],
  isAdmin: DEV_AUTH_ADMIN,
  plan: 'dev',
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * T-126: record a FRESH authentication — the one-time login signal. Fires the
 * user_logged_in analytics event AND pings POST /api/events/login (our Postgres
 * system-of-record). Called ONLY from signIn / completeNewPassword — never from the
 * mount hydration path, so a page reload / silent token refresh is not counted as a
 * login. Fire-and-forget: engagement logging must never block or break sign-in.
 */
function recordFreshLogin(method: string): void {
  track('user_logged_in', { method })
  authedFetch('/api/events/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method }),
  }).catch(() => {
    /* non-fatal: engagement logging; ignore */
  })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Hydrate from localStorage on mount — restores session across page refresh (AUTH-02)
  useEffect(() => {
    if (DEV_AUTH_BYPASS) {
      // Local dev only: inject a fake session so RequireAuth renders the app.
      setUser(DEV_BYPASS_USER)
      // Identify for analytics, but do NOT record a login — bypass is an auto-restore
      // on every reload, not a fresh authentication.
      identifyUser(DEV_BYPASS_USER)
      setLoading(false)
      return
    }
    // Load runtime Cognito config (GET /api/config.json) before checking the session,
    // so the Cognito pool/client IDs are available even though they aren't baked into
    // the bundle at build time. loadRuntimeConfig never throws (falls back to env).
    loadRuntimeConfig()
      .then(() => getSession())
      .then((session) => {
        const restored = session ? sessionToUser(session) : null
        setUser(restored)
        // Identify a restored session (returning user) but do NOT count it as a login —
        // this path is session restore / token refresh, not a fresh authentication.
        if (restored) identifyUser(restored)
      })
      .catch(() => {
        setUser(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await serviceSignIn(email, password)
    const u = sessionToUser(session)
    setUser(u)
    // Fresh authentication: identify first (so the login event attaches to the person),
    // then record the login.
    identifyUser(u)
    recordFreshLogin('password')
  }, [])

  const signOut = useCallback(async () => {
    // T-042: clearing local state must be UNCONDITIONAL. The server-side revoke
    // (globalSignOut) can fail — the T-034 prod UAT caught it rejecting and leaving the
    // app authed because the old `await serviceSignOut(); setUser(null)` skipped the clear.
    // setUser(null) now runs in finally so a revoke failure never strands the user signed in.
    try {
      if (!DEV_AUTH_BYPASS) {
        await serviceSignOut()
      }
    } catch {
      // best-effort server-side revoke; local logout below is what the user experiences
    } finally {
      setUser(null)
      // Clear the analytics identity so the next user isn't merged into this profile.
      resetAnalytics()
    }
  }, [])

  const completeNewPassword = useCallback(async (newPassword: string) => {
    const session = await serviceCompleteNewPassword(newPassword)
    const u = sessionToUser(session)
    setUser(u)
    // First-login force-change is also a fresh authentication.
    identifyUser(u)
    recordFreshLogin('new_password')
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, completeNewPassword }}>
      {children}
    </AuthContext.Provider>
  )
}
